import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import "../../../assets/styles/containers/_commentLibraryModal.scss";
import CLAddCommentModal from "../../common/TemplateUtils/CLAddCommentModal";
import { filterSelectTag, filterSelectTagIntro, preprocessHTMLContent } from "../../common/TemplateUtils/CommentLibraryHelper";
import { addNewNarrativeComment, editNarrativeComment, getCommentLibrarySummaryTagList, getCommentLibraryTagList } from "../../store/action/CommentLibraryAction";
import { timestamp } from "../../utils/services";
import CLAddCommentNHT from "../../common/TemplateUtils/CLAddCommentNHT";
import axios from "axios";
import config_settings from "../../config/config";

function AddCommentDialog(props) {
  const dispatch = useDispatch();
  const storeData = useSelector((state) => state.commentLibrary);
  const { state, setState, tagOptions, locationOptions, ratingOptions } = props;
  const { openAddDialog, commentTag, commentTitle, commentText, commentSummary, commentSummaryTag, isEdit, comment_id, commentDropdownList, summaryDropdownList, commentRating, selectedFilterTagID } = state;

  let BASE_URL;
  const origin = () => {
    let origin = window.location.origin.toLowerCase();
    if (origin.includes("https://")) origin = origin.replace("https://", "");
    else origin = origin.replace("http://", "");
    BASE_URL = config_settings[origin]?.SERVER_URL;
  };
  origin();

  const handleClose = () => {
    setState((prevState) => ({
      ...prevState,
      openAddDialog: false,
      commentTag: [],
      commentTitle: "",
      commentText: { innerHTML: "", dropdownList: [] },
      commentSummary: { innerHTML: "", dropdownList: [] },
      commentSummaryTag: [],
      commentDropdownList: [],
      commentRating: [],      // <-- reset ratings
      location: [],
      summaryDropdownList: [],
      isEdit: false,
    }));
  };

  const handleSave = async () => {
    handleClose();
    setState((prevState) => ({ ...prevState, openAddDialog: false, showLoader: true, isEdit: false }));
    let response;
    const newCommentData = {
      title: commentTitle || "",
      comment_text: filterSelectTag(commentText?.innerHTML) || "",
      summary_comment_text: filterSelectTag(commentSummary?.innerHTML) || "",
      comment_dropdowns: commentDropdownList?.length ? commentDropdownList : (commentText?.dropdownList || []),
      summary_comment_dropdowns: summaryDropdownList?.length ? summaryDropdownList : (commentSummary?.dropdownList || []),
      tag_ids: commentTag || [],
      summary_ids: commentSummaryTag || [],
      rating_ids: commentRating || [],
    };
    // Non-HT company: call POST /comments with required shape
    if (!storeData?.isHTCompany && !isEdit) {
      try {
        const companyID = sessionStorage.getItem("company-key");
        const locationString = Array.isArray(state?.location)
          ? state.location.map((l) => l?.name).filter(Boolean).join(", ")
          : (state?.location?.name || "");
        await axios.post(`${BASE_URL}/comments`,
          {
            title: commentTitle || "",
            comment: filterSelectTag(commentText?.innerHTML) || "",
            location: locationString,
            tag_ids: (commentTag || []).map((t) => t.id),
            rating_ids: (commentRating || []).map((r) => r.id),
            comment_dropdowns: commentDropdownList?.length ? commentDropdownList : (commentText?.dropdownList || []),
          },
          { headers: { "Content-Type": "application/json", "company-key": companyID } }
        );
        setState((prevState) => ({ ...prevState, showLoader: false, page: 0, refreshTs: Date.now() }));
        return;
      } catch (err) {
        console.error("ADD COMMENT API error:", err);
        setState((prevState) => ({ ...prevState, showLoader: false }));
        return;
      }
    }
    // Non-HT company EDIT: PUT /comments/{comment_id}
    if (!storeData?.isHTCompany && isEdit) {
      try {
        const companyID = sessionStorage.getItem("company-key");
        const locationString = Array.isArray(state?.location)
          ? state.location.map((l) => l?.name).filter(Boolean).join(", ")
          : (state?.location?.name || "");
        const response = await axios.put(
          `${BASE_URL}/comments/${comment_id}`,
          {
            title: commentTitle || "",
            comment: filterSelectTag(commentText?.innerHTML) || "",
            location: locationString,
            tag_ids: (commentTag || []).map((t) => t.id),
            rating_ids: (commentRating || []).map((r) => r.id),
            comment_dropdowns: commentDropdownList?.length ? commentDropdownList : (commentText?.dropdownList || []),
          },
          {
            headers: {
              "Content-Type": "application/json",
              "company-key": companyID,
            },
          }
        );
        if (response?.data) {
          const newUpdatedList = [...state.commentListData];
          const index = newUpdatedList.findIndex((item) => item.id === comment_id);
          if (index !== -1) {
            newUpdatedList[index] = {
              ...newUpdatedList[index],
              title: commentTitle,
              comment: filterSelectTag(commentText?.innerHTML) || "",
              location: locationString,
              tags: commentTag,
              ratings: commentRating,
              comment_dropdowns: commentDropdownList?.length ? commentDropdownList : (commentText?.dropdownList || []),
              updated_at: timestamp()
            };
            setState((prevState) => ({
              ...prevState,
              showLoader: false,
              commentListData: newUpdatedList,
              refreshTs: Date.now()
            }));
          } else {
            setState((prevState) => ({
              ...prevState,
              showLoader: false,
              page: 0,
              refreshTs: Date.now()
            }));
          }
        } else {
          setState((prevState) => ({
            ...prevState,
            showLoader: false,
            page: 0,
            refreshTs: Date.now()
          }));
        }
        return;
      } catch (err) {
        console.error("EDIT COMMENT API error:", err);
        setState((prevState) => ({ ...prevState, showLoader: false }));
        return;
      }
    }
    if (isEdit) {
      const newTime = timestamp();
      response = await dispatch(editNarrativeComment({ ...newCommentData, comment_id: comment_id, updated_at: newTime }));
      if (response?.payload) {
        const newUpdatedList = [...state.commentListData];
        const index = newUpdatedList.findIndex((item) => item.id === comment_id);
        if (index !== -1) {
          const buildSummaries = (comment_id, tags) =>
            (Array.isArray(tags) ? tags : []).map(t => ({
              comment_id,
              summary_id: t.id,
              summary: {
                id: t.id,
                name: t.name,
                definition: t.definition ?? null,
                color: t.color ?? null,
              },
            }));
          newUpdatedList[index] = {
            ...newUpdatedList[index],
            ...newCommentData,
            updated_at: newTime,
            tags: commentTag,
            summaries: buildSummaries(comment_id, commentSummaryTag),
            title: commentTitle,
            comment_text: filterSelectTag(commentText?.innerHTML) || "",
            summary_comment_text: filterSelectTag(commentSummary?.innerHTML) || "",
          };
          setState((prevState) => ({
            ...prevState,
            showLoader: false,
            commentListData: newUpdatedList,
            refreshTs: Date.now()
          }));
        }
      }
    } else {
      response = await dispatch(addNewNarrativeComment(newCommentData));
      if (response?.payload) {
        setState((prevState) => ({
          ...prevState,
          showLoader: false,
          commentListData: [response?.payload, ...storeData.commentLibraryList],
          refreshTs: Date.now()
        }));
      }
    }
  };

  useEffect(() => {
    if (storeData?.isHTCompany) {
      dispatch(getCommentLibrarySummaryTagList());
      dispatch(getCommentLibraryTagList())
    }
  }, [isEdit, storeData?.isHTCompany]);

  useEffect(() => {
    if (!openAddDialog || isEdit) return;
    if (Array.isArray(commentTag) && commentTag.length > 0) return;
    const filterIds = Array.isArray(selectedFilterTagID) ? selectedFilterTagID : [];
    if (filterIds.length === 0) return;
    const tagSource = storeData?.isHTCompany
      ? (storeData?.clTagList || [])
      : (tagOptions || []);
    const prefilledTags = tagSource.filter((tag) => filterIds.includes(tag?.id));
    if (prefilledTags.length === 0) return;
    setState((prevState) => ({ ...prevState, commentTag: prefilledTags }));
  }, [openAddDialog, isEdit, selectedFilterTagID, storeData?.isHTCompany, storeData?.clTagList, tagOptions]);

  return (
    <Dialog id="comment-library-dialog" disableScrollLock open={openAddDialog} maxWidth onClose={handleClose} aria-labelledby="category-modal" aria-describedby="category-modal-description">
      <DialogTitle>
        <Grid container display={"flex"}>
          <Grid item xs={11} display={"flex"} alignItems={"center"}>
            <Typography className="add-comment-title">{isEdit ? "Edit" : "New"} comment</Typography>
          </Grid>
          <Grid item xs={1} display={"flex"} justifyContent={"flex-end"} alignItems={"center"}>
            <CloseIcon className="checkbox_container" onClick={handleClose} />
          </Grid>
        </Grid>
      </DialogTitle>
      <Divider />
      <DialogContent style={{ marginX: "0px 30px !important" }} >
        {storeData?.isHTCompany ? (
          <CLAddCommentModal state={state} setState={setState} isEdit={isEdit} tagOptions={tagOptions} locationOptions={locationOptions} ratingOptions={ratingOptions} newOpen={openAddDialog} commentTag={commentTag}/>
        ) : (
          <CLAddCommentNHT state={state} setState={setState} isEdit={isEdit} tagOptions={tagOptions} locationOptions={locationOptions} ratingOptions={ratingOptions} />
        )}
      </DialogContent>
      <Divider />
      <DialogActions className="comment-library-action">
        <Grid container display={"flex"} gap={1} justifyContent={"flex-end"}>
          <Button className="back-btn" sx={{ textTransform: "none !important" }} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            disabled={
              storeData?.isHTCompany
                ? ((commentTitle?.trim() === "" &&
                  (commentText?.innerHTML?.trim() === "" ||
                    commentText?.innerHTML?.trim() === "<p><br></p>" ||
                    preprocessHTMLContent(commentText?.innerHTML) === "")))
                : (
                  commentTitle?.trim() === "" &&
                  (commentText?.innerHTML?.trim() === "" ||
                    commentText?.innerHTML?.trim() === "<p><br></p>" ||
                    preprocessHTMLContent(commentText?.innerHTML) === "")
                )
            }
            className="dialog-add-btn"
            sx={{ width: storeData?.isHTCompany ? "inherit" : "200px !important", textTransform: "none !important" }}
            onClick={() => handleSave()}>
            {storeData?.isHTCompany ? "Save" : (isEdit ? "Edit comment" : "Add new comment")}
          </Button>
        </Grid>
      </DialogActions>
    </Dialog>
  );
}

export default AddCommentDialog;